import { FC, ReactNode } from 'react'
import {
  Box, Typography, Paper, alpha, Card, CardActionArea, CircularProgress,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { MdNavigateNext } from 'react-icons/md'

export const IndicatorCard: FC<{
  title: string
  value: string | number
  icon: ReactNode
  color: string
  loading?: boolean
}> = ({ title, value, icon, color, loading }) => (
  <Paper
    variant="outlined"
    sx={{
      px: 1.5,
      py: 1.25,
      height: '100%',
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      bgcolor: 'background.paper',
      transition: 'border-color 0.2s ease',
      '&:hover': { borderColor: color },
    }}
  >
    <Box
      sx={{
        p: 0.75,
        borderRadius: 1.5,
        bgcolor: alpha(color, 0.1),
        color,
        display: 'flex',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>
        {title}
      </Typography>
      {loading ? (
        <CircularProgress size={16} sx={{ color, mt: 0.25 }} />
      ) : (
        <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
          {value}
        </Typography>
      )}
    </Box>
  </Paper>
)

export const ShortcutCard: FC<{
  title: string
  subtitle: string
  icon: ReactNode
  path: string
  color: string
  badge?: string | number
  badgeColor?: string
  disabled?: boolean
}> = ({ title, subtitle, icon, path, color, badge, badgeColor, disabled }) => {
  const navigate = useNavigate()
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 2,
        bgcolor: 'background.paper',
        opacity: disabled ? 0.7 : 1,
        transition: 'border-color 0.2s ease',
        '&:hover': {
          borderColor: disabled ? 'divider' : color,
        },
      }}
    >
      <CardActionArea
        onClick={() => !disabled && navigate(path)}
        disabled={disabled}
        sx={{ height: '100%', py: 0.75, px: 1 }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box
            sx={{
              p: 0.75,
              borderRadius: 1.5,
              bgcolor: alpha(color, 0.1),
              color,
              display: 'flex',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {subtitle}
            </Typography>
          </Box>
          {badge !== undefined && (
            <Box
              sx={{
                px: 0.75,
                py: 0.15,
                borderRadius: 1,
                bgcolor: badgeColor || color,
                color: 'white',
                fontSize: '0.65rem',
                fontWeight: 700,
                flexShrink: 0,
                minWidth: 20,
                textAlign: 'center',
              }}
            >
              {badge}
            </Box>
          )}
          {!disabled && <MdNavigateNext size={18} color={alpha('#000', 0.25)} />}
        </Box>
      </CardActionArea>
    </Card>
  )
}

export function formatUserDisplayName(username?: string): string {
  if (!username) return 'Gestor'
  const localPart = username.split('@')[0]
  const firstName = localPart.split('.')[0]
  return firstName.charAt(0).toUpperCase() + firstName.slice(1)
}
